import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';

import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import RoleBasedGuard from 'src/guards/RoleBasedGuard';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),

      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralApp /> },

        //---------------------------------------------

        {
          path: 'users',
          children: [
            { element: <Navigate to="/dashboard/users/list" replace />, index: true },
            {
              path: 'list',
              element: (
                // <RoleBasedGuard roles={["SuperAdmin"]}>
                <UserList />
                // </RoleBasedGuard>
              ),
            },
            {
              path: 'profile',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <Profile />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'profile/update-password',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <UpdateAdminPassword />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'add-user',
              element: (
                <RoleBasedGuard roles={['Admin', 'Provider', 'Agent']}>
                  <AddEditUser />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard roles={['Admin', 'Provider', 'Agent']}>
                  <AddEditUser />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'profile/edit',
              element: (
                <RoleBasedGuard roles={['Admin', 'Provider', 'Agent']}>
                  <Profile />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/charge-history',
              element: (
                <RoleBasedGuard roles={['Admin', 'Provider', 'Agent']}>
                  <UserChargeHistory />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        //---------------------------------------------

        {
          path: 'agents',
          children: [
            { element: <Navigate to="/dashboard/agents/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard hasContent roles={['Admin']}>
                  <AgentsList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'add-agent',
              element: (
                <RoleBasedGuard hasContent roles={['Admin']}>
                  <AddEditAgent />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard hasContent roles={['Admin']}>
                  <AddEditAgent />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        //---------------------------------------------

        {
          path: 'providers',
          children: [
            { element: <Navigate to="/dashboard/providers/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Agent']}>
                  <ProvidersList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'add-provider',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Agent']}>
                  <AddEditProvider />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Agent']}>
                  <AddEditProvider />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        //---------------------------------------------

        {
          path: 'provider-groups',
          children: [
            { element: <Navigate to="/dashboard/provider-groups/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard hasContent roles={['Provider', 'Admin', 'Agent']}>
                  <ProviderGroups />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'new',
              element: (
                <RoleBasedGuard hasContent roles={['Provider', 'Admin', 'Agent']}>
                  <ProviderGroupsNewEdit />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard hasContent roles={['Provider', 'Admin', 'Agent']}>
                  <ProviderGroupsNewEdit />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        //---------------------------------------------
        {
          path: 'ocpp-chargers',
          children: [
            { element: <Navigate to="/dashboard/ocpp-chargers/list" replace />, index: true },
            {
              path: 'list',
              element: <OcppChargerList />,
            },
            {
              path: 'add-ocpp-charger',
              element: <AddEditOcppCharger />,
            },
            {
              path: ':id/edit',
              element: <AddEditOcppCharger />,
            },
            {
              path: ':id/send-commands',
              element: <SendCommandPage />,
            },
            {
              path: ':id/charger-pictures',
              element: <OcppChargerPictures />,
            },
          ],
        },
        //---------------------------------------------

        {
          path: 'chargers',
          children: [
            { element: <Navigate to="/dashboard/chargers/list" replace />, index: true },
            {
              path: 'list',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargersList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'map',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <Map />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'without-location-list',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargersWithoutLocation />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit-location',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <Map />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'groups',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargerGroups />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'groups/new',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargerGroupsNewEdit />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'groups/:id/edit',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargerGroupsNewEdit />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'logs',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargersLogs />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/info',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargerInfo />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargerInfo />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'charging-sessions',
              element: (
                <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                  <ChargingSessions />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        //---------------------------------------------

        {
          path: 'payments',
          children: [
            {
              path: 'payments',
              children: [
                { element: <Navigate to="/dashboard/payments/payments/list" replace />, index: true },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <Payments />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'invoices',
              children: [
                { element: <Navigate to="/dashboard/payments/invoices/list" replace />, index: true },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <Invoices />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'charging-report',
              children: [
                {
                  element: <Navigate to="/dashboard/payments/charging-report/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <ChargingReports />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'payment-logs',
              children: [
                { element: <Navigate to="/dashboard/payments/payment-logs/list" replace />, index: true },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <PaymentLogs />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },
          ],
        },

        //---------------------------------------------

        {
          path: 'vehicles-management',
          children: [
            {
              path: 'vehicles',
              children: [
                {
                  element: <Navigate to="/dashboard/vehicles-management/vehicles/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <Vehicles />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'add-vehicle',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditVehicle />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':id/edit',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditVehicle />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            //---------------------------------------------

            {
              path: 'brands',
              children: [
                {
                  element: <Navigate to="/dashboard/vehicles-management/brands/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <VehiclesBrand />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'add-brand',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditBrand />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':id/edit',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditBrand />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            //---------------------------------------------

            {
              path: 'models',
              children: [
                {
                  element: <Navigate to="/dashboard/vehicles-management/models/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <VehiclesModel />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'add-model',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditModel />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':id/edit',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditModel />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            //---------------------------------------------

            {
              path: 'outlet-types',
              children: [
                {
                  element: <Navigate to="/dashboard/vehicles-management/outlet-types/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <OutletType />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'add-outlet-type',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditOutletType />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':id/edit',
                  element: (
                    <RoleBasedGuard roles={['Admin']} hasContent>
                      <AddEditOutletType />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },
          ],
        },

        // ---------------------------------------
        {
          path: 'credits-management',
          children: [
            {
              path: 'credits',
              children: [
                {
                  element: <Navigate to="/dashboard/credits-management/credits/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: <Credits />,
                },
              ],
            },
            {
              path: 'balance',
              children: [
                {
                  element: <Navigate to="/dashboard/credits-management/balance/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: <ListCredit />,
                },
              ],
            },
            {
              path: 'tariffs',
              children: [
                {
                  element: <Navigate to="/dashboard/credits-management/tariffs/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    // <RoleBasedGuard hasContent roles={['Admin', 'Provider']}>
                    <Tariffs />
                    // </RoleBasedGuard>
                  ),
                },
              ],
            },
          ],
        },

        //---------------------------------------------

        {
          path: 'settings',
          children: [
            {
              path: 'firmwares',
              children: [
                { element: <Navigate to="/dashboard/settings/firmwares/list" replace />, index: true },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <Firmwares />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'upload-firmware',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <UploadFirmware />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'update-firmware',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <UpdateFirmware />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'chargers',
              children: [
                { element: <Navigate to="/dashboard/settings/chargers/list" replace />, index: true },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <ChargerSettings />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'networks',
              children: [
                {
                  element: <Navigate to="/dashboard/settings/networks/update-network" replace />,
                  index: true,
                },
                // { path: 'list', element: <NetworkSettings /> },
                {
                  path: 'update-network',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <UpdateNetwork />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'taxes',
              children: [
                {
                  element: <Navigate to="/dashboard/settings/taxes/list-taxes" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <Taxes />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <DefineTaxes />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':id/update',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <DefineTaxes />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'rfid-tags',
              children: [
                {
                  element: <Navigate to="/dashboard/settings/rfid-tags/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <RfidTags />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'add',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <AddEditRfidTags />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: ':id/edit',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <AddEditRfidTags />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },

            {
              path: 'charging-profiles',
              children: [
                {
                  element: <Navigate to="/dashboard/settings/charging-profiles/list" replace />,
                  index: true,
                },
                {
                  path: 'list',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <ChargingProfiles />
                    </RoleBasedGuard>
                  ),
                },

                {
                  path: 'add',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <AddEditChargingProfile />
                    </RoleBasedGuard>
                  ),
                },

                {
                  path: ':id/edit',
                  element: (
                    <RoleBasedGuard hasContent roles={['Admin', 'Provider', 'Agent']}>
                      <AddEditChargingProfile />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <DashboardLayout />,
      children: [{ element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/Dashboard/Dashboard')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/Dashboard/GeneralAnalytics')));

// USERS
const Profile = Loadable(lazy(() => import('../pages/dashboard/UserList/Profile/ProfileCreate')));
const UpdateAdminPassword = Loadable(lazy(() => import('../pages/dashboard/UserList/Profile/UpdateAdminPassword')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList/UserTable/UserList')));
const AddEditUser = Loadable(lazy(() => import('../pages/dashboard/UserList/UserForm/UserCreate')));
const UserChargeHistory = Loadable(
  lazy(() => import('../pages/dashboard/UserList/UserChargeHistory/UserChargeHistoryTable'))
);

// AGENTS
const AgentsList = Loadable(lazy(() => import('../pages/dashboard/Agents/AgentsList')));
const AddEditAgent = Loadable(lazy(() => import('../pages/dashboard/Agents/AgentCreate')));

// VEHICLES
const Vehicles = Loadable(lazy(() => import('../pages/dashboard/VehiclesManagement/Vehicles/Vehicles')));
const AddEditVehicle = Loadable(lazy(() => import('../pages/dashboard/VehiclesManagement/Vehicles/VehicleCreate')));

// BRANDS
const VehiclesBrand = Loadable(lazy(() => import('../pages/dashboard/VehiclesManagement/VehicleBrand/VehicleBrand')));
const AddEditBrand = Loadable(
  lazy(() => import('../pages/dashboard/VehiclesManagement/VehicleBrand/VehicleBrandCreate'))
);

// MODELS
const VehiclesModel = Loadable(lazy(() => import('../pages/dashboard/VehiclesManagement/VehicleModel/VehicleModel')));
const AddEditModel = Loadable(
  lazy(() => import('../pages/dashboard/VehiclesManagement/VehicleModel/VehicleModelCreate'))
);

// OUTLETS
const OutletType = Loadable(lazy(() => import('../pages/dashboard/VehiclesManagement/OutletType/OutletType')));
const AddEditOutletType = Loadable(
  lazy(() => import('../pages/dashboard/VehiclesManagement/OutletType/OutletTypeCreate'))
);

// PROVIDERS
const ProvidersList = Loadable(lazy(() => import('../pages/dashboard/ProviderList/ProvidersList')));
const AddEditProvider = Loadable(lazy(() => import('../pages/dashboard/ProviderList/ProviderCreate')));
const ProviderGroups = Loadable(lazy(() => import('../pages/dashboard/ProviderList/Groups/Groups')));
const ProviderGroupsNewEdit = Loadable(lazy(() => import('../pages/dashboard/ProviderList/Groups/GroupsCreate')));

// CHARGERS
const ChargersList = Loadable(
  lazy(() => import('../pages/dashboard/ChargersManagement/Chargers/ChargersTable/ChargersTable'))
);
const ChargersWithoutLocation = Loadable(
  lazy(() =>
    import('../pages/dashboard/ChargersManagement/ChargersWithoutProvisioning/ChargersWithoutProvisioningTable')
  )
);
const ChargersLogs = Loadable(lazy(() => import('../pages/dashboard/ChargersManagement/Logs/Logs')));
const ChargerInfo = Loadable(lazy(() => import('../pages/dashboard/ChargersManagement/Chargers/ChargersCreate')));
const ChargingSessions = Loadable(
  lazy(() => import('../pages/dashboard/ChargersManagement/ChargingSessions/ChargingSessionsCreate'))
);
const ChargerGroups = Loadable(lazy(() => import('../pages/dashboard/ChargersManagement/Groups/Groups')));
const ChargerGroupsNewEdit = Loadable(lazy(() => import('../pages/dashboard/ChargersManagement/Groups/GroupsCreate')));

// MAP
const Map = Loadable(lazy(() => import('../pages/dashboard/ChargersManagement/Map/MapCreate')));

// SETTINGS
const Firmwares = Loadable(lazy(() => import('../pages/dashboard/Settings/Firmwares/FirmwareTable/FirmwaresList')));
const ChargerSettings = Loadable(lazy(() => import('../pages/dashboard/Settings/Chargers/ChargerSettingsCreate')));
const UploadFirmware = Loadable(
  lazy(() => import('../pages/dashboard/Settings/Firmwares/UploadFirmware/UploadFirmwareCreate'))
);
const UpdateFirmware = Loadable(
  lazy(() => import('../pages/dashboard/Settings/Firmwares/UpdateFirmware/UpdateFirmwaresList'))
);
const UpdateNetwork = Loadable(
  lazy(() => import('../pages/dashboard/Settings/Networks/UpdateNetwork/UpdateNetworkCreate'))
);
const Taxes = Loadable(lazy(() => import('../pages/dashboard/Settings/Taxes/Taxes')));
const DefineTaxes = Loadable(lazy(() => import('../pages/dashboard/Settings/Taxes/DefineTaxes/DefineTaxesCreate')));
const RfidTags = Loadable(lazy(() => import('../pages/dashboard/Settings/RfidTags/RfidTags')));
const AddEditRfidTags = Loadable(
  lazy(() => import('../pages/dashboard/Settings/RfidTags/RfidTagsForm/RfidTagsCreate'))
);

// PAYMENTS
const Payments = Loadable(lazy(() => import('../pages/dashboard/Payments/Payments/PaymentsList')));
const Invoices = Loadable(lazy(() => import('../pages/dashboard/Payments/Invoices/InvoicesList')));
const PaymentLogs = Loadable(lazy(() => import('../pages/dashboard/Payments/PaymentLogs/PaymentLogsList')));
const ChargingReports = Loadable(lazy(() => import('../pages/dashboard/Payments/Report/ChargingReport')));

const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

// CREDITS
const Credits = Loadable(lazy(() => import('../pages/dashboard/WalletManagement/CreditsSelleds/CreditsList')));
const ListCredit = Loadable(lazy(() => import('../pages/dashboard/WalletManagement/CreditsSelleds/ListCredit')));
const Tariffs = Loadable(lazy(() => import('../pages/dashboard/WalletManagement/Tariff/TariffList')));

const OcppChargerList = Loadable(lazy(() => import('../pages/dashboard/OcppChargers/OcppChargerList')));
const AddEditOcppCharger = Loadable(lazy(() => import('../pages/dashboard/OcppChargers/AddEditOcppCharger')));
const SendCommandPage = Loadable(lazy(() => import('../pages/dashboard/OcppChargers/SendCommandPage')));
const OcppChargerPictures = Loadable(
  lazy(() => import('../pages/dashboard/OcppChargers/OcppCharger/ConfigurationTable/OcppChargerPictures'))
);

// Charging Profiles
const ChargingProfiles = Loadable(lazy(() => import('../pages/dashboard/ChargingProfiles/ChargingProfiles')));
const AddEditChargingProfile = Loadable(
  lazy(() => import('../pages/dashboard/ChargingProfiles/ChargingProfileCreate'))
);
